"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { WorkingLine } from "@/components/tools/stats-clinical/data-grid";
import {
  computeFromFields,
  type DosageContext,
  type DosageFields,
  type DosageMode,
  INITIAL_FIELDS,
} from "@/components/tools/stats-clinical/dosage-compute";
import {
  ConvertFields,
  DesiredHaveFields,
  WeightFields,
} from "@/components/tools/stats-clinical/dosage-fields";
import { DosagePractice } from "@/components/tools/stats-clinical/dosage-practice";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const DOSAGE_CALCULATION_CALCULATOR_STRINGS = {
  disclaimerHeading: "Practice calculator — not for patient care",
  disclaimer:
    "This page is for learning dosage-calculation math. It has no drug information, checks no safe dose range and knows nothing about any real order or patient. Real doses come from the prescriber's order and a drug reference, with an independent double-check.",
  modeLabel: "What do you want to work out?",
  modeDesiredHave: "Amount to give: desired ÷ have × quantity",
  modeWeight: "A weight-based dose (per kg)",
  modeConvert: "A unit conversion",
  desiredLabel: "Desired dose (the order)",
  haveLabel: "Dose on hand (the supply)",
  quantityLabel: "Quantity the supply is in",
  quantityHint:
    "For 250 mg per tablet enter 1 tablet; for 25 mg per 5 mL enter 5 mL.",
  formLabel: "Supplied as",
  formTablet: "one: tablet|other: tablets",
  formCapsule: "one: capsule|other: capsules",
  unitUnits: "units",
  unitMeq: "mEq",
  unitLabel: "Unit",
  orderLabel: "Ordered dose per kg",
  orderUnitLabel: "Dose unit",
  basisLabel: "The order is",
  basisDose: "Per kg, per dose",
  basisDay: "Per kg, per day",
  dosesLabel: "Doses per day",
  weightLabel: "Body weight",
  weightUnitLabel: "Weight unit",
  supplyHeading: "Optional: liquid supply, to find the volume",
  supplyHave: "Dose on hand",
  supplyUnit: "Unit of the supply",
  supplyQuantity: "Per this many mL",
  convertValue: "Value to convert",
  convertFrom: "From",
  convertTo: "To",
  roundingLabel: "Round the answer to",
  roundWhole: "A whole number",
  roundOne: "1 decimal",
  roundTwo: "2 decimals",
  invalid:
    "Enter positive numbers in every box, and make sure the units can be compared: mass with mass, volume with volume, and units or mEq only with the same unit.",
  enterPrompt: "Enter an order and a supply to see the working.",
  results: "Results",
  cardAmount: "Amount to give",
  cardWeight: "Weight in kg",
  cardPerDay: "Dose per day",
  cardPerDose: "Dose per dose",
  cardVolume: "Volume per dose",
  cardConverted: "Converted value",
  workingHeading: "Working, with the units",
  stepConvert:
    "Convert so the units match: {value} {from} × {factor} {to}/{from} = {result} {to}",
  stepDesiredHave:
    "({desired} {unit} ÷ {have} {unit}) × {quantity} {form} = {raw} {answerForm} — the {unit} cancel, leaving {answerForm}",
  stepWeight: "{pounds} lb ÷ 2.2 lb/kg = {kilograms} kg",
  stepDosePerKg:
    "{perKg} {unit}/kg × {kilograms} kg = {total} {unit} — the kg cancel",
  stepSplit:
    "{total} {unit}/day ÷ {doses} doses/day = {perDose} {unit}/dose — the days cancel",
  stepVolume:
    "({dose} {unit} ÷ {have} {unit}) × {quantity} mL = {volume} mL — the {unit} cancel, leaving mL",
  stepConversion:
    "{value} {from} × ({factor} {to} ÷ 1 {from}) = {result} {to} — the {from} cancel",
  factorsNote:
    "Factors used: 1 kg = 1,000 g, 1 g = 1,000 mg, 1 mg = 1,000 mcg, 1 kg = 2.2 lb, 1 L = 1,000 mL, 1 tsp = 5 mL, 1 tbsp = 15 mL, 1 fl oz = 30 mL, 1 cup = 240 mL. The last four, and 2.2, are teaching approximations.",
  roundingNote:
    "Rounding rules depend on the form and the course: tablets are often given in halves, and liquids to a measurable volume. Check yours.",
  practiceHeading: "Practice problem {number} of {total}",
  problemDh:
    "A practice order is {desired} {desiredUnit}. The supply is {have} {haveUnit} per {quantity} {form}. How much do you give?",
  problemWeight:
    "A practice order is {perKg} {unit} per kg per dose. The child weighs {weight} {weightUnit}. The liquid supply is {have} {haveUnit} per {quantity} mL. How many mL per dose?",
  problemDay:
    "A practice order is {perKg} {unit} per kg per day in {doses} equal doses. The child weighs {weight} kg. How many {unit} in each dose?",
  problemConvert: "Convert {value} {from} to {to}.",
  showAnswer: "Show the working",
  hideAnswer: "Hide the working",
  nextProblem: "Next problem",
  answerLine: "Answer: {answer}",
  practiceNote:
    "All practice orders are invented for the exercise and name no drug.",
} as const;

export default function DosageCalculationCalculatorWidget() {
  const strings = useToolUi(DOSAGE_CALCULATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<DosageMode>("desiredHave");
  const [fields, setFields] = useState<DosageFields>(INITIAL_FIELDS);
  const set =
    <K extends keyof DosageFields>(key: K) =>
    (value: DosageFields[K]) =>
      setFields((current) => ({ ...current, [key]: value }));

  const ctx: DosageContext = {
    strings,
    fmt,
    locale,
    decimals: Number(fields.rounding),
  };
  const output = computeFromFields(mode, fields, ctx);
  const entered = [
    fields.desired,
    fields.have,
    fields.perKg,
    fields.weight,
    fields.convertValue,
  ].some((v) => v.trim() !== "");

  return (
    <div className="space-y-6">
      <Notice>
        <strong>{strings.disclaimerHeading}.</strong> {strings.disclaimer}
      </Notice>
      <PillGroup
        label={strings.modeLabel}
        name="dosage-mode"
        onChange={setMode}
        options={[
          { value: "desiredHave", label: strings.modeDesiredHave },
          { value: "weight", label: strings.modeWeight },
          { value: "convert", label: strings.modeConvert },
        ]}
        value={mode}
      />
      {mode === "desiredHave" ? (
        <DesiredHaveFields
          fields={fields}
          locale={locale}
          set={set}
          strings={strings}
        />
      ) : null}
      {mode === "weight" ? (
        <WeightFields fields={fields} set={set} strings={strings} />
      ) : null}
      {mode === "convert" ? (
        <ConvertFields fields={fields} set={set} strings={strings} />
      ) : null}
      <div className="max-w-xs">
        <SelectField
          id="dosage-round"
          label={strings.roundingLabel}
          onChange={set("rounding")}
          options={[
            { value: "0", label: strings.roundWhole },
            { value: "1", label: strings.roundOne },
            { value: "2", label: strings.roundTwo },
          ]}
          value={fields.rounding}
        />
      </div>
      {entered && !output ? <ErrorNotice>{strings.invalid}</ErrorNotice> : null}
      {output ? (
        <div aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <StatGrid>
            {output.cards.map(([label, value], index) => (
              <StatCard
                emphasis={index === output.cards.length - 1}
                key={label}
                label={label}
                value={value}
              />
            ))}
          </StatGrid>
          <div className={`${CARD} space-y-2`}>
            <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
            {output.steps.map((step) => (
              <WorkingLine key={step}>{step}</WorkingLine>
            ))}
            <p className="text-muted-foreground text-sm">
              {strings.factorsNote}
            </p>
            {mode === "convert" ? null : (
              <p className="text-muted-foreground text-sm">
                {strings.roundingNote}
              </p>
            )}
          </div>
        </div>
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
      <DosagePractice fmt={fmt} locale={locale} strings={strings} />
    </div>
  );
}
